import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getPostById, savePost } from '../../lib/store';
import { slugify, calculateReadingTime } from '../../lib/utils';
import SafeIcon from '../../common/SafeIcon';
import { FiArrowLeft, FiSave } from 'react-icons/fi';

const Editor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '', // mapped from teaser in UI
    content: '',
    category: 'Strategie',
    focus_keyword: '', // mapped from keyword in UI
    author: 'Marc Arndt',
    date: new Date().toISOString().substring(0, 10),
    is_published: true,
    read_time: '5'
  });

  useEffect(() => {
    const isAuth = localStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }

    if (id) {
      const fetchPost = async () => {
        const post = await getPostById(id);
        if (post) {
          setFormData({
            ...post,
            date: post.date.substring(0, 10)
          });
        }
      };
      fetchPost();
    }
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updated = { 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      };
      
      // Auto-generate slug from title if it's a new post
      if (name === 'title' && !id) {
        updated.slug = slugify(value);
      }
      
      // Auto-calculate read time when content changes
      if (name === 'content') {
        updated.read_time = calculateReadingTime(value).toString();
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const postToSave = {
        ...formData,
        id: id || null,
        date: new Date(formData.date).toISOString()
      };
      
      await savePost(postToSave);
      navigate('/admin/dashboard');
    } catch (error) {
      alert('Fehler beim Speichern: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{id ? 'Beitrag bearbeiten' : 'Neuer Beitrag'} | Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="mb-6">
        <button 
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-2 text-slate-500 hover:text-finance-blue transition"
        >
          <SafeIcon icon={FiArrowLeft} /> Zurück
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
        <h2 className="text-2xl font-bold text-finance-blue mb-6">
          {id ? 'Beitrag bearbeiten' : 'Neuen Beitrag verfassen'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Titel</label>
              <input 
                type="text" name="title" required
                value={formData.title} onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-finance-green outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Slug (URL)</label>
              <input 
                type="text" name="slug" required
                value={formData.slug} onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-finance-green outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Kategorie</label>
              <select 
                name="category" required
                value={formData.category} onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-finance-green outline-none"
              >
                <option value="Strategie">Strategie</option>
                <option value="Steuern & Recht">Steuern & Recht</option>
                <option value="Aktienanalysen">Aktienanalysen</option>
                <option value="Mindset">Mindset</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Fokus Keyword (SEO)</label>
              <input 
                type="text" name="focus_keyword" required
                value={formData.focus_keyword} onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-finance-green outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Veröffentlichungsdatum</label>
              <input 
                type="date" name="date" required
                value={formData.date} onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-finance-green outline-none"
              />
            </div>

            <div className="flex items-center gap-2 pt-8">
              <input 
                type="checkbox" name="is_published" id="is_published"
                checked={formData.is_published} onChange={handleChange}
                className="w-4 h-4 text-finance-green border-slate-300 rounded focus:ring-finance-green"
              />
              <label htmlFor="is_published" className="text-sm font-medium text-slate-700">Öffentlich sichtbar</label>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Teaser (SEO Kurzbeschreibung)</label>
              <textarea 
                name="description" rows="2" required
                value={formData.description} onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-finance-green outline-none"
              />
            </div>

            <div className="md:col-span-2">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-slate-700">Inhalt (Markdown unterstützt)</label>
                <span className="text-xs text-slate-400">{formData.read_time} Min. Lesezeit</span>
              </div>
              <textarea 
                name="content" rows="15" required
                value={formData.content} onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-finance-green outline-none font-mono text-sm"
                placeholder="## Überschrift 2&#10;&#10;Dein Text hier..."
              />
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button 
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-finance-green hover:bg-emerald-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-md font-semibold transition"
            >
              <SafeIcon icon={FiSave} /> {loading ? 'Speichert...' : 'Speichern & Veröffentlichen'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Editor;